<template>
    <div class="assistent-generating-card">
        <div class="assistent-generating-card__header">
            <flex-container
                size="auto"
                align-items="center"
                gap="sm"
            >
                <div class="assistent-generating-card__icon">
                    <svg-icon
                        :icon="WindowFlipIcon"
                        color="white"
                        size="sm"
                        is-custom-icon
                    />
                </div>
                <text-decorator size="lg" style="font-weight: 500">
                    {{ props.assistent.brand.websiteDomain }}
                </text-decorator>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Aantal pagina's
                </text-decorator>
                <div class="large">
                    {{ crawlResultsCount > 0 ? crawlResultsCount : '-' }}
                </div>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Laatste scan
                </text-decorator>
                <flex-container
                    gap="sm"
                    align-items="end"
                    no-wrap
                >
                    <template v-if="lastCrawledAt">
                        <div class="large">
                            {{ formatDate(lastCrawledAt) }}
                        </div>
                        <flex-container gap="xs" class="time">
                            <svg-icon
                                :icon="ClockIcon"
                                color="gray-300"
                                size="xxs"
                                is-custom-icon
                            />
                            {{ formatTime(lastCrawledAt) }}
                        </flex-container>
                    </template>
                    <div v-else class="large">
                        -
                    </div>
                </flex-container>
            </flex-container>
            <flex-container
                size="auto"
                direction="column"
                gap="xs"
            >
                <text-decorator size="xxs">
                    Laatste wijziging
                </text-decorator>
                <flex-container
                    gap="sm"
                    align-items="end"
                    no-wrap
                >
                    <template v-if="lastUpdatedAt">
                        <div class="large">
                            {{ formatDate(lastUpdatedAt) }}
                        </div>
                        <flex-container gap="xs" class="time">
                            <svg-icon
                                :icon="ClockIcon"
                                color="gray-300"
                                size="xxs"
                                is-custom-icon
                            />
                            {{ formatTime(lastUpdatedAt) }}
                        </flex-container>
                    </template>
                    <div v-else class="large">
                        -
                    </div>
                </flex-container>
            </flex-container>
            <flex-container
                size="auto"
                gap="xs"
                align-items="center"
                justify-content="end"
            >
                <bar-skeleton
                    width="69"
                    height="20"
                />
                <bar-skeleton
                    width="20"
                    height="20"
                />
            </flex-container>
        </div>
        <div class="assistent-generating-card__content">
            <div class="assistent-generating-card__layer4">
                <bar-skeleton
                    width="145"
                    height="20"
                    dark
                />

                <bar-skeleton
                    width="20"
                    height="20"
                    dark
                />
            </div>
            <div class="assistent-generating-card__layer3">
                Geen data beschikbaar
            </div>
            <div class="assistent-generating-card__layer2">
                <flex-container
                    direction="column"
                    align-items="center"
                    size="auto"
                >
                    <flex-container
                        direction="column"
                        align-items="center"
                        size="auto"
                        gap="xs"
                        no-wrap
                    >
                        <bar-skeleton
                            width="30"
                            height="53"
                        />
                        <bar-skeleton
                            width="66"
                            height="13"
                        />
                    </flex-container>

                    <flex-container size="auto">
                        <bar-skeleton
                            width="222"
                            height="42"
                        />
                    </flex-container>
                </flex-container>
            </div>
            <div class="assistent-generating-card__layer1">
                <div class="assistent-generating-card__avatar">
                    <avatar-placeholder />
                </div>
                <div class="assistent-generating-card__settings-button">
                    <button-block-icon
                        :icon="GearIcon"
                        size="xs"
                        is-custom-icon
                        @click="onClickSettings"
                    />
                </div>
                <div class="assistent-generating-card__status" :class="statusClasses">
                    {{ assistent.status.label }}
                </div>
                <div class="assistent-generating-card__name">
                    {{ assistent.name }}
                </div>
            </div>
        </div>
    </div>

    <assistent-generating-dialog ref="assistentGeneratingDialog" />
    <assistent-delete-dialog ref="assistentDeleteDialog" @confirm="onDeleteConfirmed" />
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { formatDate, formatTime } from '@/shared/utils/date';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import { type Assistent, CrawlResult, DeleteAssistentCommand } from '@keyvue/one-api-client';

    import WindowFlipIcon from '@keyvue/one-awesome-icons/icons/regular/window-flip.svg?component';
    import ClockIcon from '@keyvue/one-awesome-icons/icons/regular/clock.svg?component';
    import GearIcon from '@keyvue/one-awesome-icons/icons/regular/gear.svg?component';
    import AssistentGeneratingDialog from '@/ai/assistent/components/dialog/AssistentGeneratingDialog.vue';
    import AssistentDeleteDialog from '@/ai/assistent/components/dialog/AssistentDeleteDialog.vue';
    import AssistentDeletedEvent, { type AssistentDeletedEventParameters } from '@/ai/assistent/events/AssistentDeletedEvent';
    import AvatarPlaceholder from '@/ai/assistent/assets/avatar_placeholder.svg';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';
    import useEventBus from '@/services/eventbus/composables/useEventBus';

    const eventBus = useEventBus();
    const { notifyDeleted, notifyError } = useNotify();
    const { client, getLazyResourceCollection } = useClient();

    const props = defineProps<{
        assistent: Assistent,
    }>();

    const assistentGeneratingDialog = ref<InstanceType<typeof AssistentGeneratingDialog>>();
    const assistentDeleteDialog = ref<InstanceType<typeof AssistentDeleteDialog>>();

    const lastCrawledAtCollection = getLazyResourceCollection(CrawlResult, {
        'order[crawledAt]': 'desc',
        'itemsPerPage': 1,
        'pagination': true,
        'brand': props.assistent.brand.id,
    });

    const lastUpdatedAtCollection = getLazyResourceCollection(CrawlResult, {
        'order[updatedAt]': 'desc',
        'itemsPerPage': 1,
        'pagination': true,
        'brand': props.assistent.brand.id,
    });

    const crawlResultsCount = computed(() => {
        if (lastCrawledAtCollection.total.value > 0) {
            return lastCrawledAtCollection.total.value;
        }

        if (lastUpdatedAtCollection.total.value > 0) {
            return lastUpdatedAtCollection.total.value;
        }

        return 0;
    });

    const lastCrawledAt = computed(() => {
        if (lastCrawledAtCollection.data.value?.length === 0) {
            return null;
        }

        return lastCrawledAtCollection.data.value?.[0].crawledAt;
    });

    const lastUpdatedAt = computed(() => {
        if (lastUpdatedAtCollection.data.value?.length === 0) {
            return null;
        }

        return lastUpdatedAtCollection.data.value?.[0].updatedAt;
    });

    const statusClasses = computed(() => {
        return {
            'assistent-generating-card__status': true,
            [`assistent-generating-card__status--${props.assistent.status}`]: true,
            [`assistent-generating-card__status--${props.assistent.active ? 'active' : 'inactive'}`]: true,
        };
    });

    async function onDeleteConfirmed() {
        const { name } = props.assistent;

        try {
            await client.dispatch(
                DeleteAssistentCommand.fromAssistent(
                    props.assistent,
                ),
            );

            notifyDeleted(`De assistent ${name} is verwijderd`);
            eventBus.dispatch<AssistentDeletedEventParameters>(AssistentDeletedEvent, {
                assistentId: props.assistent.id.value,
            });
        } catch (error) {
            notifyError(
                new Error(`We konden de assistent ${name} niet verwijderen`),
            );
        }
    }

    function onClickSettings() {
        assistentGeneratingDialog.value?.dialog?.openOverlay();
    }

    onMounted(() => {
        lastCrawledAtCollection.load();
        lastUpdatedAtCollection.load();
    });
</script>

<style scoped lang="scss">
    .assistent-generating-card {
        background: var(--theme-colors-default--white);
        display: grid;
        grid-template-rows: auto auto;
        border-radius: size(sm);
        overflow: hidden;

        .small {
            font-size: 80%;
        }

        .large {
            font-size: 130%;
            font-weight: 500;
            white-space: nowrap;
        }

        .time {
            font-size: 80%;
            font-weight: 500;
        }

        &__avatar {
            position: absolute;
            height: 100%;
            width: 100%;
            display: flex;
            align-items: end;
            justify-content: center;
        }

        &__settings-button {
            position: absolute;
            inset: 0;
            display: flex;
            padding: size(sm);
            align-items: center;
            justify-content: end;

            :deep(svg) {
                width: size(md);
                height: size(md);
            }
        }

        &__status {
            position: absolute;
            color: var(--theme-colors-default--white);
            font-size: size(sm);
            padding: size(sm);
            border-bottom-right-radius: size(xs);

            &--ready {
                background-color: var(--theme-colors-default--green-500);
            }

            &--active {
                background-color: var(--theme-colors-default--green-500);
            }

            &--inactive {
                background-color: var(--theme-colors-default--red-500);
            }

            &--crawling {
                background-color: var(--theme-colors-default--orange-500);
            }

            &--analyzing {
                background-color: var(--theme-colors-default--orange-500);
            }

            &--generating {
                background-color: var(--theme-colors-default--orange-500);
            }
        }

        &__name {
            position: absolute;
            bottom: 0;
            left: 0;
            padding: size(sm);
            color: var(--theme-colors-default--white);
            text-shadow: 0 0 .25rem rgba(0, 0, 0, 0.75);
            font-weight: 500;
            font-size: size(md);
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;
            width: calc(100% - 65px);
        }

        &__icon {
            width: size(lg);
            height: size(lg);
            background-color: var(--theme-colors-default--turquoise-500);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &__show-live-icon {
            width: size(md);
            height: size(md);
            background: var(--theme-colors-default--gray-400);
            border-radius: 50%;
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;

            :deep(svg) {
                width: size(sm);
                height: size(sm);
            }
        }

        &__header {
            display: grid;
            grid-template-columns: auto auto auto auto 1fr;
            gap: size(lg);
            padding: size(md);
            color: var(--theme-colors-default--gray-500);
            align-items: center;
        }

        &__content {
            position: relative;
            min-height: 170px;
            overflow: hidden;
            box-shadow: 3px 0 3px 0 rgba(0,0,0,.2);
        }

        &__layer1, &__layer2, &__layer3, &__layer4 {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            overflow: hidden;
            background-color: var(--theme-colors-default--white);;
            box-shadow: size(xs) 0 10px -5px rgba(0,0,0,.2);
            display: flex;
        }

        &__layer1 {
            right: 80%;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            background: linear-gradient(to bottom, #d0e8f5, #dfe6ea);

            &::after {
                content: "";
                position: absolute;
                inset: 0;
                background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.1) 100%);
                pointer-events: none;
            }
        }

        &__layer2 {
            right: 54%;
            padding-left: 20%;
            padding-right: 25px;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            justify-content: center;
            align-items: center;
        }

        &__layer3 {
            right: 17%;
            padding-left: 40%;
            border-top-right-radius: 200px;
            border-bottom-right-radius: 200px;
            justify-content: center;
            align-items: center;
        }

        &__layer4 {
            right: 0;
            padding-left: 83%;
            background: var(--theme-colors-default--primary);
            justify-content: center;
            align-items: center;
            display: flex;
            gap: size(sm);
        }
    }
</style>
